import { Box, Typography, Paper, Button } from '@mui/material';
import ConstructionIcon from '@mui/icons-material/Construction';
import LockIcon from '@mui/icons-material/Lock';
import { useNavigate } from 'react-router-dom';
import { ReactNode } from 'react';

interface FeatureDisabledOverlayProps {
  featureName: string;
  children: ReactNode;
  showHRRedirect?: boolean;
}

export function FeatureDisabledOverlay({
  featureName,
  children,
  showHRRedirect = true
}: FeatureDisabledOverlayProps) {
  const navigate = useNavigate();

  return (
    <Box sx={{ position: 'relative' }}>
      {/* The actual content - visible but with overlay */}
      <Box
        sx={{
          filter: 'blur(2px)',
          opacity: 0.6,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        {children}
      </Box>

      {/* Overlay message */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 4,
            textAlign: 'center',
            maxWidth: 450,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #FFFFFF 0%, #FDF2F8 100%)',
            border: '2px solid #EC4899',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <Box
              sx={{
                p: 2,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
                border: '2px solid #F59E0B',
              }}
            >
              <ConstructionIcon sx={{ fontSize: 40, color: '#D97706' }} />
            </Box>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1F2937', mb: 1 }}>
            {featureName} - Coming Soon
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
            <LockIcon sx={{ fontSize: 16, color: '#9CA3AF' }} />
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              This feature is currently in development
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: '#4B5563', mb: 3 }}>
            We're working hard to bring you this feature. In the meantime, you can use the HR module which is fully functional.
          </Typography>

          {showHRRedirect && (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={() => navigate('/employees')}
                sx={{
                  background: 'linear-gradient(135deg, #EC4899 0%, #8B5CF6 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #DB2777 0%, #7C3AED 100%)',
                  },
                }}
              >
                Go to HR Module
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

export default FeatureDisabledOverlay;
